/**
 * PORTED — RO-3-06 / #44299.
 * Origin: `excellent/app/operations/_dashboard.tsx` + `_ledger-client.tsx`.
 *
 * Cause A rewrite: every vault read moves to `operations.dashboard`. The Next
 * original is a server component that calls `listMoneyAccounts` /
 * `listTransactions` / `listJobs` / `listApplications` / `listPipelineStages` /
 * `listAllScopedTasksByStatus` in render. This file is a pure client consumer
 * via `useApi`. Navigation uses the router seam (never `next/*`).
 *
 * MoneyLedger stays prop-driven (same shape as the Next client shell) — the
 * dashboard hands it pre-gathered rows after the one fetch.
 */

import * as React from "react";
import {
  Band,
  Card,
  Section,
  ScopedTaskLifecycleList,
  formatDate,
  formatUsd,
  cn,
  Ledger,
  MetricStrip,
  type DataTableColumn,
} from "@excellent/ui";
import type { ScopedTask } from "@excellent/ui/types/projects";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  operations,
  type OperationsLedgerRole,
  type OperationsLedgerTxn,
} from "@excellent/ui/api/contracts/operations";

type LedgerScope = "cash" | "burn" | "roles";

const TX_COLUMNS: DataTableColumn<OperationsLedgerTxn>[] = [
  {
    key: "date",
    header: "Date",
    width: "w-3/12",
    render: (tx) => <span className="text-muted-foreground">{formatDate(tx.at)}</span>,
  },
  {
    key: "description",
    header: "Description",
    width: "w-5/12",
    render: (tx) => <span className="truncate text-foreground">{tx.description}</span>,
  },
  {
    key: "amount",
    header: "Amount",
    width: "w-4/12",
    render: (tx) => (
      <span className={cn(tx.amount > 0 ? "text-success" : "text-foreground")}>
        {formatUsd(tx.amount)}
      </span>
    ),
  },
];

const ROLE_COLUMNS: DataTableColumn<OperationsLedgerRole>[] = [
  {
    key: "title",
    header: "Role",
    width: "w-5/12",
    render: (r) => <span className="truncate text-foreground">{r.title}</span>,
  },
  {
    key: "opened",
    header: "Open since",
    width: "w-3/12",
    render: (r) => (
      <span className="text-muted-foreground">
        {r.openedAt ? formatDate(r.openedAt) : "—"}
      </span>
    ),
  },
  {
    key: "pipeline",
    header: "Pipeline",
    width: "w-4/12",
    render: (r) => (
      <span className="truncate type-subtext tabular-nums text-muted-foreground">{r.pipeline}</span>
    ),
  },
];

function MoneyLedger({
  cashLabel,
  burnLabel,
  openRoles,
  recentTxns,
  spendTxns,
  roles,
}: {
  cashLabel: string;
  burnLabel: string;
  openRoles: number;
  recentTxns: OperationsLedgerTxn[];
  spendTxns: OperationsLedgerTxn[];
  roles: OperationsLedgerRole[];
}) {
  const [scope, setScope] = React.useState<LedgerScope>("cash");

  const strip = (
    <MetricStrip
      cells={[
        { key: "cash", label: "Cash", value: cashLabel },
        { key: "burn", label: "Monthly burn", value: burnLabel },
        { key: "roles", label: "Open roles", value: String(openRoles) },
      ]}
      selected={scope}
      onSelect={(k) => setScope(k as LedgerScope)}
    />
  );

  if (scope === "roles") {
    return (
      <Ledger<OperationsLedgerRole>
        strip={strip}
        rows={roles}
        columns={ROLE_COLUMNS}
        rowKey={(r) => r.id}
        tableClassName="table-fixed"
        empty="No open roles — nothing hiring right now."
      />
    );
  }

  const rows = scope === "burn" ? spendTxns : recentTxns;
  return (
    <Ledger<OperationsLedgerTxn>
      strip={strip}
      rows={rows}
      columns={TX_COLUMNS}
      rowKey={(tx) => tx.id}
      tableClassName="table-fixed"
      empty={
        scope === "burn"
          ? "No spend recorded — connect finance to see burn."
          : "No transactions yet — connect finance to see the ledger."
      }
    />
  );
}

/**
 * Question-first Operations dashboard (SYSTEM.md §5.3) — "is the company
 * funded, compliant & staffed?" One fetch, then the same layout as the Next
 * server component.
 */
export function OperationsDashboard() {
  const { data, isLoading, error } = useApi(operations.dashboard, {});

  if (isLoading && !data) {
    return (
      <Band>
        <p className="type-body text-muted-foreground">Loading operations…</p>
      </Band>
    );
  }
  if (error || !data) {
    return (
      <Band>
        <p className="type-body text-destructive">
          Could not load the operations dashboard.
        </p>
      </Band>
    );
  }

  const {
    hasMoney,
    cash,
    monthlyBurn,
    runwayMonths,
    runwayEnds,
    recentTxns,
    spendTxns,
    roles,
    hasHiring,
    openJobs,
    candidateCount,
    offersOut,
    thinPipeline,
    tasks,
  } = data;

  // ScopedTaskLifecycleList wants the full ScopedTask type; the DTO is a
  // structural subset that the list actually reads.
  const opsTasks = tasks as unknown as ScopedTask[];

  return (
    <Band>
      <div className="flex flex-col gap-6">
        {hasMoney && runwayMonths != null ? (
          <p className="type-heading font-medium text-foreground">
            {Math.round(runwayMonths)} months of runway
            <span className="text-muted-foreground">
              {" "}
              — ends ~{formatDate(runwayEnds)}.
            </span>
          </p>
        ) : (
          <p className="type-body text-muted-foreground">
            No accounts connected —{" "}
            <Link
              href="/operations?area=finance"
              className="text-foreground hover:underline"
            >
              connect finance
            </Link>{" "}
            to see runway.
          </p>
        )}
        <MoneyLedger
          cashLabel={formatUsd(cash)}
          burnLabel={formatUsd(monthlyBurn)}
          openRoles={openJobs}
          recentTxns={recentTxns}
          spendTxns={spendTxns}
          roles={roles}
        />
      </div>

      {hasHiring ? (
        <Section label="Hiring">
          <p className="flex items-center gap-2.5 type-body text-foreground">
            {thinPipeline ? (
              <span aria-hidden className="size-2 shrink-0 rounded-full bg-warning" />
            ) : null}
            {openJobs} {openJobs === 1 ? "role" : "roles"} open · {candidateCount}{" "}
            {candidateCount === 1 ? "candidate" : "candidates"} · {offersOut}{" "}
            {offersOut === 1 ? "offer" : "offers"} out
          </p>
        </Section>
      ) : null}

      <Section label="Issues">
        <Card className="gap-0 overflow-hidden py-0">
          <ScopedTaskLifecycleList
            tasks={opsTasks}
            empty="No open operations issues."
            gutter
          />
        </Card>
      </Section>
    </Band>
  );
}
